import type { EmployeeData } from '@interfaces/hiring-manager.model';
import { NAVIGATION_ROUTES } from './navigation-routes.constants';

export interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  relatedRoutes?: string[];
  show: (
    user: EmployeeData | null | undefined,
    role: string,
    hasAccessToRoute?: (url: string) => boolean,
  ) => boolean;
  children?: MenuItem[];
}

export class MenuConfigHelper {
  static getMenuConfig(
    user: EmployeeData | null | undefined,
    role: string,
    hasAccessToRoute?: (url: string) => boolean,
  ): MenuItem[] {
    const isOpsTeam = !!user?.isOpsTeam;
    const isReporties = !!user?.isReporties;
    const isIOP = role === 'IOP';
    const isVendor = role === 'vendor';
    const isCWR = role === 'CWR';
    const isEmployee = role === 'EMPLOYEE';
    const isFinanceAdmin = !!user?.isFinanceAdmin;
    const isScrumMaster = !!user?.isScrumMaster;

    // Vendor users: Vendor Dashboard plus the view-only iSkill Team Explorer
    // for their own vendor's people (Oct 2026).
    if (isVendor) {
      return [
        {
          label: 'Vendor Dashboard',
          icon: 'dashboard',
          route: NAVIGATION_ROUTES.VENDORFLOW_DASHBOARD,
          show: () => true,
        },
        {
          label: 'iSkill',
          icon: 'psychology',
          route: NAVIGATION_ROUTES.ISKILL,
          // Per-contact opt-in, set by Ops on Access Management (iskill_access).
          show: () => !!user?.iskillAccess,
        },
      ];
    }

    // Scrum masters are usually individual contributors or contractors —
    // exactly the personas served by the early-return menus below, which
    // never reach the main list's Productivity item. Declare it once here
    // and append wherever isScrumMaster is set.
    const productivityItem: MenuItem = {
      label: 'Productivity',
      icon: 'speed',
      route: NAVIGATION_ROUTES.PRODUCTIVITY,
      show: () => isScrumMaster,
    };

    // Scrum-master-only users (flag enabled, no reportees, no ops duties) get a
    // focused menu — iSkill + Productivity. Vendor Management (EMP without
    // reportees) reads as noise for this persona; CWR scrum masters keep
    // Employee Dashboard, which is where they submit their own timesheet.
    if (isScrumMaster && !isReporties && !isOpsTeam && !isIOP) {
      return [
        ...(isCWR
          ? [{
              label: 'Employee Dashboard',
              icon: 'dashboard',
              route: NAVIGATION_ROUTES.VENDOR_EMPLOYEE,
              show: () => true,
            }]
          : []),
        {
          label: 'iSkill',
          icon: 'psychology',
          route: NAVIGATION_ROUTES.ISKILL,
          show: () => true,
        },
        productivityItem,
      ];
    }

    // Regular CBRE employees (non-leaders) — iSkill (+ Productivity for scrum
    // masters). An EMPLOYEE with active reportees (leader level missing on
    // their record) falls through to the leader menu below instead, matching
    // the login routing that sends reportee-havers to Team Dashboard.
    if (isEmployee && !isReporties) {
      return [
        {
          label: 'iSkill',
          icon: 'psychology',
          route: NAVIGATION_ROUTES.ISKILL,
          show: () => true,
        },
        ...(isScrumMaster ? [productivityItem] : []),
      ];
    }

    // Contractors — existing dashboard + iSkill (+ Productivity for scrum masters)
    if (isCWR) {
      return [
        {
          label: 'Employee Dashboard',
          icon: 'dashboard',
          route: NAVIGATION_ROUTES.VENDOR_EMPLOYEE,
          show: () => true,
        },
        {
          label: 'iSkill',
          icon: 'psychology',
          route: NAVIGATION_ROUTES.ISKILL,
          show: () => true,
        },
        ...(isScrumMaster ? [productivityItem] : []),
      ];
    }

    return [
      {
        label: 'Team Dashboard',
        icon: 'groups_1',
        route: NAVIGATION_ROUTES.TEAM_MANAGEMENT,
        show: () => isReporties || isIOP,
      },
      {
        label: 'Vendor Management',
        icon: 'assignment_ind',
        show: () => true,
        children: [
          {
            label: 'Vendor Dashboard',
            icon: 'dashboard',
            route: NAVIGATION_ROUTES.VENDORFLOW_DASHBOARD,
             relatedRoutes: ['/vendor'],
            show: (user, role, hasAccessToRoute) => {
              const hasRouteAccess = hasAccessToRoute
                ? hasAccessToRoute(NAVIGATION_ROUTES.VENDORFLOW_DASHBOARD)
                : true;
              return hasRouteAccess && (isIOP || (isReporties && isOpsTeam));
            },
          },
          {
            label: 'Vendor Employee',
            icon: 'dashboard',
            route: NAVIGATION_ROUTES.VENDOR_EMPLOYEE,
            show: (user, role, hasAccessToRoute) => {
              const hasRouteAccess = hasAccessToRoute
                ? hasAccessToRoute(NAVIGATION_ROUTES.VENDOR_EMPLOYEE)
                : true;
              return hasRouteAccess && isIOP;
            },
          },
          {
            label: 'Hiring Manager',
            icon: 'manage_accounts',
            route: NAVIGATION_ROUTES.VENDOR_MANAGEMENT_HIRING,
            relatedRoutes: ['/hiring-manager'],
            show: (user, role, hasAccessToRoute) => {
              const hasRouteAccess = hasAccessToRoute
                ? hasAccessToRoute(NAVIGATION_ROUTES.VENDOR_MANAGEMENT_HIRING)
                : true;
              // Hiring Manager is a leader-only surface — someone with no
              // reportees sees empty tables + zero counters, which reads as
              // broken. Same gate as Team Dashboard.
              return hasRouteAccess && (isReporties || isIOP);
            },
          },
          {
            label: 'OPS Team',
            icon: 'groups_3',
            route: NAVIGATION_ROUTES.OPS_VENDOR_MANAGEMENT,
            show: (user, role, hasAccessToRoute) => {
              const hasRouteAccess = hasAccessToRoute
                ? hasAccessToRoute(NAVIGATION_ROUTES.OPS_VENDOR_MANAGEMENT)
                : true;
              return (isOpsTeam || isIOP) && hasRouteAccess;
            },
          },
        ],
      },
      {
        label: 'Asset Management',
        icon: 'account_balance',
        route: NAVIGATION_ROUTES.ASSET_MANAGEMENT,
        show: (user, role, hasAccessToRoute) => {
          const hasRouteAccess = hasAccessToRoute
            ? hasAccessToRoute(NAVIGATION_ROUTES.ASSET_MANAGEMENT)
            : true;
          return hasRouteAccess && (isOpsTeam || isIOP);
        },
      },
      {
        label: 'iSkill',
        icon: 'psychology',
        route: NAVIGATION_ROUTES.ISKILL,
        show: () => {
          // iSkill is available to all CBRE employees (L2-L5)
          // Role-based scoping is handled by the iSkill auth guard
          return !isVendor && !isCWR;
        },
      },
      {
        label: 'Finance',
        icon: 'savings',
        route: NAVIGATION_ROUTES.FINANCE,
        show: (user, role, hasAccessToRoute) => {
          const hasRouteAccess = hasAccessToRoute
            ? hasAccessToRoute(NAVIGATION_ROUTES.FINANCE)
            : true;
          return hasRouteAccess && isFinanceAdmin;
        },
      },
      {
        label: 'Productivity',
        icon: 'speed',
        route: NAVIGATION_ROUTES.PRODUCTIVITY,
        show: (user, role, hasAccessToRoute) => {
          const hasRouteAccess = hasAccessToRoute
            ? hasAccessToRoute(NAVIGATION_ROUTES.PRODUCTIVITY)
            : true;
          // Scrum masters can upload; leaders / ops / IOP can view the team dashboard.
          return hasRouteAccess && (isScrumMaster || isReporties || isOpsTeam || isIOP);
        },
      },
    ];
  }

  static filterMenuItems(
    menuItems: MenuItem[],
    user: EmployeeData | null | undefined,
    role: string,
    hasAccessToRoute?: (url: string) => boolean,
  ): MenuItem[] {
    return menuItems
      .map((item) => {
        if (item.children) {
          const filteredChildren = item.children.filter((child) =>
            child.show(user, role, hasAccessToRoute),
          );
          return {
            ...item,
            children: filteredChildren,
          };
        }
        return item;
      })
      .filter((item) => item.show(user, role, hasAccessToRoute));
  }

  static collectAllRoutes(items: MenuItem[]): string[] {
    const routes: string[] = [];

    for (const item of items) {
      if (item.route) {
        routes.push(item.route);
      }
      if (item.children) {
        routes.push(...MenuConfigHelper.collectAllRoutes(item.children));
      }
    }

    return routes;
  }
}
